<template>
    <div id="profileForm">
        <div class="w-full mx-auto">
            <div class="my-6 alert alert-danger border-2 shadow-md" v-if="isEmptyEmail">
                <h3>Please update your email address as it appears to be empty. We need a valid email address to communicate important information about your order, and we won't be able to proceed with any action until we have it. Thank you for your attention to this matter.</h3>
            </div>

            <div id="profilePersonalInfo">
                <div class="subsection flex flex-wrap">
                    <div class="inline-block subsection-title"><i class="far fa-id-card mr-2"></i>Personal Info</div>
                </div>

                <form class="w-full mt-4 mb-8">
                    <div class="flex flex-wrap mb-2">
                        <div class="w-full px-3 mb-4 sm:mb-0 md:mb-0">
                            <div class="flex flex-wrap">
                                <div class="w-full sm:w-1/2 pr-0 sm:pr-3">
                                    <label class="form-label-class" for="first-name">First Name*</label>
                                    <input v-model="personalInfo.first_name" class="form-input" id="first-name" type="text" placeholder="" :class="{ 'is-invalid': errors && errors.first_name }" :disabled="editProfileType !== 'personalInfo'" />
                                </div>
                                <div class="w-full sm:w-1/2">
                                    <label class="form-label-class" for="last-name">Last Name*</label>
                                    <div class="w-full flex justify-center">
                                        <input v-model="personalInfo.last_name" class="flex-1 xs:inline-flex form-input-inline-w-none mr-1" id="last-name" type="text" placeholder="" :class="{ 'is-invalid': errors && errors.last_name }" :disabled="editProfileType !== 'personalInfo'" />
                                        <button v-if="editProfileType !== 'personalInfo' && !isEmptyEmail" @click.prevent="editProfile('personalInfo')" class="btn btn-sm btn-yellow-ghost btn-shadow-none btn-icon" v-tooltip="{ content: 'Edit Personal Information', theme: 'yellow' }" aria-label="Edit Personal Info"><i class="far fa-edit"></i></button>
                                    </div>
                                </div>
                            </div>
                            <div class="flex justify-center profile-actions mb-4 xs:mt-4" v-if="editProfileType == 'personalInfo'">
                                <button class="btn btn-sm btn-yellow-ghost mx-1 xs:ml-0" @click="close">Cancel</button>
                                <button class="btn btn-sm btn-yellow mx-1 trade-tracker-form-save" :disabled="isUpdating" @click="update('contact')">Update</button>
                            </div>
                        </div>
                    </div>
                    <div class="flex flex-wrap mb-2">
                        <div class="w-full px-3">
                            <div class="flex flex-wrap border-t md:border-t-0 pt-4 md:pt-0">
                                <div class="w-full md:w-1/3 pr-0 md:pr-3">
                                    <label class="form-label-class" for="email">Email Address</label>
                                    <div class="w-full flex justify-center bg-white py-1" :class="{ 'bg-yellow-100 p-1 rounded': editProfileType == 'personalInfoEmail' }">
                                        <input v-model="personalInfo.email" class="flex-1 form-input-inline-w-none mr-1" id="email" type="text" placeholder="Enter your email" :disabled="editProfileType !== 'personalInfoEmail'" :class="{ 'is-invalid': errors && errors.email }" />
                                        <template v-if="editProfileType == 'personalInfoEmail'">
                                            <button :disabled="isUpdating" @click.prevent="update('email')" class="btn btn-sm btn-yellow btn-shadow-none btn-icon mr-1" aria-label="Save">
                                                <i class="far fa-save"></i>
                                            </button>
                                            <button @click.prevent="close" class="btn btn-sm btn-white-ghost btn-shadow-none btn-icon" aria-label="Close Save">
                                                <i class="fas fa-times text-gray-700"></i>
                                            </button>
                                        </template>
                                        <button v-else @click.prevent="editProfile('personalInfoEmail')" class="btn btn-sm btn-yellow-ghost btn-shadow-none btn-icon" v-tooltip="{ content: 'Edit your email address', theme: 'yellow' }" aria-label="Edit Personal Email">
                                            <i class="far fa-envelope"></i>
                                        </button>
                                    </div>
                                </div>
                                <div class="w-full md:w-1/3 pr-0 md:pr-3">
                                    <label class="form-label-class" for="username">Username</label>
                                    <div class="w-full flex justify-center bg-white py-1" :class="{ 'bg-yellow-100 p-1 rounded': editProfileType == 'personalInfoUsername' }">
                                        <input v-model="personalInfo.username" class="flex-1 form-input-inline-w-none mr-1" id="username" type="text" placeholder="Enter your username" :disabled="editProfileType !== 'personalInfoUsername'" :class="{ 'is-invalid': errors && errors.username }" />
                                        <span v-if="!isEmptyEmail">
                                            <template v-if="editProfileType == 'personalInfoUsername'">
                                                <button :disabled="isUpdating" @click.prevent="update('username')" class="btn btn-sm btn-yellow btn-shadow-none btn-icon mr-1" aria-label="Edit Username">
                                                    <i class="far fa-save"></i>
                                                </button>
                                                <button @click.prevent="close" class="btn btn-sm btn-white-ghost btn-shadow-none btn-icon" aria-label="Close Username">
                                                    <i class="fas fa-times text-gray-700"></i>
                                                </button>
                                            </template>
                                            <button v-else @click.prevent="editProfile('personalInfoUsername')" class="btn btn-sm btn-yellow-ghost btn-shadow-none btn-icon" v-tooltip="{ content: 'Edit your Username', theme: 'yellow' }" aria-label="Edit your Username">
                                                <i class="far fa-user"></i>
                                            </button>
                                        </span>
                                    </div>
                                </div>
                                <div class="w-full md:w-1/3">
                                    <label class="form-label-class" for="password">Password</label>
                                    <div class="w-full flex justify-center bg-white py-1">
                                        <input class="flex-1 form-input-inline-w-none mr-1" id="password" type="password" placeholder="●●●●●●●" disabled />
                                        <button v-if="!isEmptyEmail" @click.prevent="changePasswordModal = true" class="btn btn-sm btn-yellow-ghost btn-shadow-none btn-icon" v-tooltip="{ content: 'Change Password', theme: 'yellow' }" aria-label="Change password">
                                            <i class="fas fa-key"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </form>
            </div>

            <div id="profileOrdersInfo" v-if="ordersInfo.orders.length && !isEmptyEmail">
                <div class="subsection flex flex-wrap">
                    <div class="inline-block subsection-title"><i class="far fa-credit-card mr-2"></i>Subscription Info</div>
                </div>

                <div class="w-full mb-8 mt-4">
                    <p class="text-sm mb-6 text-gray-700">This is a list of your current subscriptions. Click the "Modify Credit Card" button to enter your credit card information.</p>
                    <div class="table-responsive">
                        <table class="table table-hover">
                            <tbody>
                                <tr v-for="order in orderedOrders" :key="order.order_id">
                                    <td class="w-auto">
                                        <span class="inline-block xs:block w-15 mr-2 xs:mr-0 xs:mb-1 bg-gray-100 rounded-lg py-px px-2 leading-normal text-xs text-gray-500 text-center cursor-pointer" :class="{ 'bg-yellow-500 text-white': order.order_status_code == 0 }" v-tooltip="{ content: getOrderStatusTooltip(order), theme: 'light' }">
                                            {{ order.order_status || 'N/A' }}
                                        </span>
                                    </td>
                                    <td class="w-full text-left">
                                        <span class="inline-block font-semibold">{{ order.product_description || 'N/A' }}</span>
                                    </td>
                                    <td class="min-w-xxxs xs:min-w-6">
                                        <button v-if="order.notices_requested_payments == 'False'" @click.prevent="updateEmailBilling(order)" class="btn btn-xs btn-shadow-none whitespace-nowrap btn-green-ghost" aria-label="Email Billing Notifications">
                                            <i class="far fa-square"></i>
                                            <span class="ml-1">Email Billing Notifications</span>
                                        </button>
                                        <button v-if="order.notices_requested_payments == 'True'" @click.prevent="updateEmailBilling(order)" class="btn btn-xs btn-shadow-none whitespace-nowrap btn-green" aria-label="Email Billing Notifications">
                                            <i class="fas fa-check-square"></i>
                                            <span class="ml-1">Email Billing Notifications</span>
                                        </button>
                                    </td>
                                    <td class="w-full text-left" nowrap>
                                        <span v-if="order.order_status_code !== 0" @click.prevent="openCancelOrderModal(order)" class="inline-block xs:block w-15 mr-2 xs:mr-0 xs:mb-1 rounded-lg py-px px-2 leading-normal text-xs text-center cursor-pointer bg-red-500 text-white" v-tooltip="{ content: 'Cancel your current order', placement: 'top', theme: 'red' }">
                                            Cancel
                                        </span>
                                        <span v-if="order.creditcard && order.creditcard.id">
                                            <span class="inline-block xs:mb-1 text-gray-700 rounded-lg mr-1 py-px px-2 leading-normal text-xs text-center cursor-pointer" :class="{ 'bg-red-100 border border-red-200': isCreditCardExpired(order), 'bg-green-100 border border-green-200': !isCreditCardExpired(order) }">
                                                {{ getCreditCardInfo(order) }}
                                            </span>
                                            <button @click.prevent="openPaymentModal(order)" class="btn btn-xs btn-yellow-ghost btn-shadow-none" aria-label="Modify Card Details">
                                                <i class="far fa-edit"></i>
                                                <span>Modify Card Details</span>
                                            </button>
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div id="profileMailingAddress" v-if="!isEmptyEmail">
                <div class="subsection flex flex-wrap xs:mt-8">
                    <div class="inline-block subsection-title"><i class="fas fa-map-marked-alt mr-2"></i>Mailing Address</div>
                    <div class="inline-block ml-auto">
                        <span @click="editProfile('mailingAddress')" class="btn btn-xs btn-yellow-ghost btn-shadow-none profile-btn-edit"><i class="far fa-edit"></i><b class="ml-1">Edit</b></span>
                    </div>
                </div>

                <form class="w-full">
                    <fieldset :disabled="editProfileType !== 'mailingAddress'" role="group" aria-label="Mailing Address">
                        <div class="flex flex-wrap mb-2 mt-4">
                            <div class="w-full md:w-1/2 px-3">
                                <label class="form-label-class" for="address">Street Address*</label>
                                <input v-model="mailingAddress.address" class="form-input" id="address" type="text" placeholder="" :class="{ 'is-invalid': errors && errors.address }" />
                            </div>
                            <div class="w-full md:w-1/2 px-3">
                                <label class="form-label-class" for="address2">Apartment, Suite, etc.</label>
                                <input v-model="mailingAddress.address2" class="form-input" id="address2" type="text" placeholder="" />
                            </div>
                            <div class="w-full md:w-1/2 px-3">
                                <label class="form-label-class" for="city">City*</label>
                                <input v-model="mailingAddress.city" class="form-input" id="city" type="text" placeholder="" :class="{ 'is-invalid': errors && errors.city }" />
                            </div>
                            <div class="w-full md:w-1/2 px-3">
                                <div class="flex flex-wrap mt-px">
                                    <div v-if="states[mailingAddress.country]" class="w-2/3 pr-3 -mt-px">
                                        <label class="form-label-class -mt-px" for="state">State*</label>
                                        <select v-model="mailingAddress.state" class="form-control form-control-no-shadow" id="state" :class="{ 'is-invalid': errors && errors.state }">
                                            <option value="">Select One</option>
                                            <option :value="code" v-for="(state, code) in states[mailingAddress.country]" :key="code">{{ state }}</option>
                                        </select>
                                    </div>
                                    <div class="w-1/3 -mt-px">
                                        <label class="form-label-class -mt-px" for="zip-code">Postal Code*</label>
                                        <input v-model="mailingAddress.zip" class="form-input" id="zip-code" type="text" placeholder="" :class="{ 'is-invalid': errors && errors.zip }" />
                                    </div>
                                </div>
                            </div>
                            <div class="w-full md:w-1/2 px-3">
                                <label class="form-label-class" for="country">Country*</label>
                                <select @change="mailingAddress.state = ''" v-model="mailingAddress.country" class="form-control form-control-no-shadow" id="country" :class="{ 'is-invalid': errors && errors.country }">
                                    <option :value="country.code" v-for="(country, key) in countries" :key="key">{{ country.name }}</option>
                                </select>
                            </div>
                            <div class="w-full md:w-1/2 px-3 mt-3 md:mt-0">
                                <label class="form-label-class" for="phone">Phone*</label>
                                <input v-model="mailingAddress.phone" class="form-input" id="phone" type="phone" placeholder="" :class="{ 'is-invalid': errors && errors.phone }" />
                            </div>
                        </div>
                        <div class="flex justify-center profile-actions">
                            <button class="btn btn-sm btn-yellow-ghost mx-1 xs:ml-0" @click="close">Cancel</button>
                            <button class="btn btn-sm btn-yellow mx-1 trade-tracker-form-save" :disabled="isUpdating" @click="update('address')">Update</button>
                        </div>
                    </fieldset>
                </form>
            </div>

            <div id="profileInvoicesInfo" v-if="ordersInfo.invoices && Object.keys(ordersInfo.invoices).length">
                <div class="subsection flex flex-wrap xs:mt-8">
                    <div class="inline-block subsection-title"><i class="fas fa-coins mr-2"></i>Payment History</div>
                    <!-- <div class="inline-block ml-auto">
                        <button
                            @click.prevent="updateEmailBillings()"
                            class="btn btn-xs btn-shadow-none whitespace-nowrap"
                            :class="{'btn-yellow-ghost': !emailBillingNotifications,  'btn-green': emailBillingNotifications}"
                            aria-label="Email Billing Notifications"
                        >
                            <i class="fas fa-check-square text-sm mr-1" v-if="emailBillingNotifications"></i>
                            <i class="far fa-square text-sm mr-1" v-if="!emailBillingNotifications"></i>
                            <span class="text-sm">Email Billing Notifications</span>
                        </button>
                    </div> -->
                </div>

                <div class="table-scroller pt-4 mb-6">
                    <table class="table table-hover _table-striped w-full">
                        <thead>
                            <tr class="bg-blue-500 text-white">
                                <th style="text-align:left;">Payment Date</th>
                                <th style="text-align:left;">Product Description</th>
                                <th>Billed To</th>
                                <th>Amount Billed</th>
                                <th>Download</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(invoice, index) in ordersInfo.invoices" :key="index">
                                <td class="text-left">{{ invoice.date }}</td>
                                <td class="text-left">{{ invoice.product_description }}</td>
                                <td class="text-left">
                                    <span class="inline-block xs:mb-1 text-gray-700 rounded-lg py-px px-2 leading-normal text-xxs text-center cursor-pointer bg-green-100">
                                        {{ invoice.type }}
                                        {{ invoice.card_type }}
                                        {{ invoice.last_four }}
                                    </span>
                                </td>
                                <td>${{ invoice.amount }}</td>
                                <td>
                                    <a @click.prevent="getInvoice(invoice.date_invoice)" class="btn btn-xs btn-yellow-ghost btn-shadow-none whitespace-nowrap"><i class="fas fa-download mr-1"></i>Download</a>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <modal @close="closePasswordModal" v-if="changePasswordModal" className="max-w-sm w-full">
                <template #header>
                    <div class="text-yellow-500">
                        <i class="fas fa-key mr-1"></i>
                        Update Password
                    </div>
                </template>
                <template #body>
                    <div class="w-full py-4">
                        <div class="flex flex-wrap">
                            <div class="w-full mb-1">
                                <label class="form-label-class" for="current-password">Current Password</label>
                                <input v-model="changePassword.current_password" class="form-input" id="current-password" type="password" :class="{ 'is-invalid': changePasswordError && changePasswordError.current_password }" />
                            </div>
                            <div class="w-full mb-1">
                                <label class="form-label-class" for="new-password">New Password</label>
                                <input v-model="changePassword.password" class="form-input" id="new-password" type="password" :class="{ 'is-invalid': changePasswordError && changePasswordError.password }" />
                            </div>
                            <div class="w-full">
                                <label class="form-label-class" for="confirm-password">Confirm New Password</label>
                                <input v-model="changePassword.password_confirmation" class="form-input" id="confirm-password" type="password" :class="{ 'is-invalid': changePasswordError && changePasswordError.password_confirmation }" />
                            </div>
                        </div>
                        <div class="w-full p-0 mb-3 bg-red-500 px-2 py-1 text-white text-xs text-center" v-if="changePasswordError">
                            <template v-for="error in changePasswordError">
                                {{ error[0] }}<br />
                            </template>
                        </div>
                    </div>
                </template>

                <template #footer>
                    <div class="flex justify-center">
                        <button class="btn btn-sm btn-yellow-ghost mx-1" @click="closePasswordModal">Cancel</button>
                        <button class="btn btn-sm btn-yellow mx-1" :disabled="isUpdating" @click="updatePassword">Update Password</button>
                    </div>
                </template>
            </modal>

            <modal @close="closePaymentModal" v-if="updatePaymentModal" className="max-w-lg w-full">
                <template #header>
                    <div class="text-yellow-500">
                        <i class="far fa-credit-card mr-1"></i>
                        Modify Card Details
                    </div>
                </template>
                <template #body>
                    <div class="w-full pt-4 pb-6">
                        <div class="w-full bg-tan-200 text-center py-2 border mb-2 border-gray-200">
                            <p class="text-sm text-gray-600 mb-1">
                                Modify card details for
                            </p>
                            <p class="text-sm font-bold leading-none">«{{ updatePaymentModal }}»</p>
                        </div>
                        <div class="w-full mt-4 font-bold text-lg border-b"><i class="fas fa-map-marked-alt mr-2"></i>Card Information</div>
                        <div class="w-full mt-4">
                            <div class="flex flex-wrap">
                                <div class="w-full sm:w-1/2 pr-0 sm:pr-3 -mt-px">
                                    <label class="form-label-class" for="ccname">Name on Card<sup>*</sup></label>
                                    <input v-model="updatePayment.ccname" class="form-input" id="ccname" type="text" placeholder="Full Name" :class="{ 'is-invalid': updatePaymentError && updatePaymentError.ccname }" autocomplete="off" />
                                </div>
                                <div class="w-full sm:w-1/2 -mt-px">
                                    <label class="form-label-class" for="ccnum">Card Number<sup>*</sup></label>
                                    <input v-model="updatePayment.ccnum" @input="enforceNumeric" class="form-input" id="ccnum" type="text" maxlength="24" placeholder="Enter the card number" :class="{ 'is-invalid': updatePaymentError && updatePaymentError.ccnum }" autocomplete="off" />
                                </div>
                            </div>
                        </div>
                        <div class="w-full">
                            <div class="flex flex-wrap">
                                <div class="w-full sm:w-2/5 pr-0 sm:pr-3 -mt-px">
                                    <label class="form-label-class -mt-px" for="ccexp">Expiration Month<sup>*</sup></label>
                                    <select v-model="updatePayment.ccexpmonth" class="form-control form-control-no-shadow" id="ccexpmonth" :class="{ 'is-invalid': updatePaymentError && updatePaymentError.ccexpmonth }" autocomplete="off">
                                        <option value="">Month</option>
                                        <option v-for="(month, key) in ccexpmonths" :key="key" :value="month">{{ getFullMonth(month) }}</option>
                                    </select>
                                </div>
                                <div class="w-full sm:w-2/5 pr-0 sm:pr-3 mt-4 sm:-mt-px">
                                    <label class="form-label-class -mt-px" for="ccexpyear">Expiration Year<sup>*</sup></label>
                                    <input v-model="updatePayment.ccexpyear" class="form-input" type="text" maxlength="4" id="ccexpyear" placeholder="Expiration Year" :class="{ 'is-invalid': updatePaymentError && updatePaymentError.ccexpyear }" autocomplete="off" />
                                </div>
                                <div class="w-full sm:w-1/5 -mt-px">
                                    <label class="form-label-class -mt-px" for="ccvercode">CID&nbsp;Code<sup>*</sup></label>
                                    <input v-model="updatePayment.ccvercode" @input="validateCIDCode" class="form-input" id="ccvercode" type="text" maxlength="4" placeholder="CID Code" :class="{ 'is-invalid': updatePaymentError && updatePaymentError.ccvercode }" autocomplete="off" />
                                </div>
                            </div>
                        </div>
                        <div class="w-full mt-4">
                            <div class="w-full font-bold text-lg border-b"><i class="fas fa-map-marked-alt mr-2"></i>Billing Address</div>
                            <div class="flex flex-wrap mb-2 mt-4 -mx-3">
                                <div class="w-full md:w-1/2 px-3">
                                    <label class="form-label-class" for="address">Street Address*</label>
                                    <input v-model="updatePayment.baaddress" class="form-input" id="address" type="text" placeholder="" :class="{ 'is-invalid': updatePaymentError && updatePaymentError.baaddress }" />
                                </div>
                                <div class="w-full md:w-1/2 px-3">
                                    <label class="form-label-class" for="address2">Apartment, Suite, etc.</label>
                                    <input v-model="updatePayment.baaddress2" class="form-input" id="address2" type="text" placeholder="" />
                                </div>
                                <div class="w-full md:w-1/2 px-3">
                                    <label class="form-label-class" for="city">City*</label>
                                    <input v-model="updatePayment.bacity" class="form-input" id="city" type="text" placeholder="" :class="{ 'is-invalid': updatePaymentError && updatePaymentError.bacity }" />
                                </div>
                                <div class="w-full md:w-1/2 px-3">
                                    <div class="flex flex-wrap mt-px">
                                        <div v-if="states[updatePayment.bacountry]" class="w-2/3 pr-3 -mt-px">
                                            <label class="form-label-class -mt-px" for="state">State*</label>
                                            <select v-model="updatePayment.bastate" class="form-control form-control-no-shadow" id="state" :class="{ 'is-invalid': updatePaymentError && updatePaymentError.bastate }">
                                                <option value="">Select One</option>
                                                <option :value="code" v-for="(state, code) in states[updatePayment.bacountry]" :key="code">{{ state }}</option>
                                            </select>
                                        </div>
                                        <div class="w-1/3 -mt-px">
                                            <label class="form-label-class -mt-px" for="zip-code">Postal Code*</label>
                                            <input v-model="updatePayment.bazip" class="form-input" id="zip-code" type="text" placeholder="" :class="{ 'is-invalid': updatePaymentError && updatePaymentError.bazip }" />
                                        </div>
                                    </div>
                                </div>
                                <div class="w-full md:w-1/2 px-3">
                                    <label class="form-label-class" for="country">Country*</label>
                                    <select @change="updatePayment.bastate = ''" v-model="updatePayment.bacountry" class="form-control form-control-no-shadow" id="country" :class="{ 'is-invalid': updatePaymentError && updatePaymentError.bacountry }">
                                        <option :value="country.code" v-for="(country, key) in countries" :key="key">{{ country.name }}</option>
                                    </select>
                                </div>
                                <div class="w-full md:w-1/2 px-3 mt-3 md:mt-0">
                                    <label class="form-label-class" for="phone">Phone*</label>
                                    <input v-model="updatePayment.baphone" class="form-input" id="phone" type="phone" placeholder="" :class="{ 'is-invalid': updatePaymentError && updatePaymentError.baphone }" />
                                </div>
                            </div>
                        </div>
                    </div>
                </template>

                <template #footer>
                    <div class="flex justify-center">
                        <button class="btn btn-sm btn-yellow-ghost mx-1" @click="closePaymentModal">Cancel</button>
                        <button class="btn btn-sm btn-yellow mx-1" :disabled="isUpdating" @click="savePayment">Save</button>
                    </div>
                </template>
            </modal>

            <modal @close="closeCancelOrderModal" v-if="cancelOrderModal" className="max-w-md w-full">
                <template #header>
                    <div class="text-red-500">
                        <i class="far fa-credit-card mr-1"></i>
                        Order Cancellation
                    </div>
                </template>
                <template #body>
                    <div class="w-full pb-3">
                        <div class="w-full mb-2">
                            <div class="w-full mb-4">
                                <div class="text-lg font-bold">
                                    Cancel Order: {{ cancelOrderModal.product_description || 'CURRENT' }}
                                </div>
                                <div class="w-full text-xs italic" v-if="!cancelOrderConfirmed">
                                    Status: {{ cancelOrderModal.order_status }}, Created: {{ cancelOrderModal.order_date }}, Order ID: {{ cancelOrderModal.order_id }}
                                </div>
                            </div>
                            <div class="w-full" v-if="!cancelOrderConfirmed">
                                <div class="mb-2">We’re sorry to hear you’d like to proceed with this cancellation. You will receive a confirmation email once the cancellation is processed.</div>
                                <div class="mb-4">We want to ensure all your questions about this order are addressed and that you’ve fully explored all features. If you have any concerns or if there’s anything we can assist you with, please feel free to <a href="/contact">Contact Us</a>.</div>
                                <div class="w-full text-sm italic">Cancellations during a trial period are immediate and end the subscription upon request. For Monthly/Prepaid subscriptions, cancellations will prevent the next billing cycle.</div>
                            </div>
                            <div class="w-full" v-if="cancelOrderConfirmed">
                                <div v-if="cancelOrderError">
                                    <p class="mb-2 font-medium text-red-700">We apologize, but your cancellation request could not be processed automatically.</p>
                                    <p>{{ cancelOrderError }}</p>
                                </div>
                                <div v-if="!cancelOrderError">
                                    <p>{{ cancelOrderConfirmed }}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>

                <template #footer>
                    <div class="flex justify-center" v-if="cancelOrderInProgress && !cancelOrderConfirmed">
                        <button class="btn btn-sm btn-blue mx-2">
                            <span class="flex items-center">
                                <i class="fas fa-spinner fa-spin !mr-2"></i>
                                Processing your request...
                            </span>
                        </button>
                    </div>
                    <div class="flex justify-center" v-if="!cancelOrderInProgress && !cancelOrderConfirmed">
                        <button class="btn btn-sm btn-blue mx-2" @click="closeCancelOrderModal">Keep Order</button>
                        <button class="btn btn-sm btn-red mx-2" @click="updateCancelOrder">Cancel Order</button>
                    </div>
                    <div class="flex justify-center" v-if="cancelOrderConfirmed">
                        <button class="btn btn-sm btn-blue mx-2" @click="closeCancelOrderModal">OK</button>
                    </div>
                </template>
            </modal>

        </div>

        <flash-message ref="flash" :seconds-to-display="3"></flash-message>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
    initialForm: Object,
    countries: Array,
    states: Object,
});

const cctypes = {
    'VISA': 'VISA',
    'MC': 'Mastercard',
    'AMEX': 'American Express',
    'DSC': 'Discover'
};

const flash = ref(null);
const contactData = ref({});
const mailingAddressData = ref({});
const creditcardsData = ref({});
const ordersData = ref({});
const invoicesData = ref({});

if (props.initialForm) {
    contactData.value = props.initialForm.contact || {};
    creditcardsData.value = props.initialForm.creditcards || null;
    ordersData.value = props.initialForm.orders || null;

    // Sort invoices
    if (props.initialForm.invoices) {
        invoicesData.value = Object.fromEntries(
            Object.entries(props.initialForm.invoices).sort((a, b) => parseInt(b[0]) - parseInt(a[0]))
        );
    }

    // Find the default 'Mailing Address'
    mailingAddressData.value = props.initialForm.addresses.find(mAddress => mAddress.default_shipping === 'True') || {};

    mailingAddressData.value.country = Object.keys(props.states).find(mCountry => props.states[mCountry][mailingAddressData.value.state]);
    if (!mailingAddressData.value.country) {
        mailingAddressData.value.country = props.countries.find(mCountry => mCountry['code'] === mailingAddressData.value.state).code;
    }
}

const errors = ref({});
const editProfileType = ref(false);
const isEditMode = ref(false);
const isUpdating = ref(false);
const isEmptyEmail = ref(!contactData.value.banker_email);
const cancelOrderInProgress = ref(false);
const cancelOrderConfirmed = ref(false);

const updatePaymentModal = ref(false);
const updatePaymentError = ref('');
const updatePayment = ref({
    ccname: null,
    cctype: '',
    ccvercode: null,
    ccnum: null,
    ccexpmonth: '',
    ccexpyear: '',
    ccexp: '',
    baaddress: '',
    baaddress2: '',
    bacity: '',
    bastate: '',
    bacountry: '',
    bazip: '',
    baphone: '',
});

const changePasswordModal = ref(false);
const changePasswordError = ref('');
const changePassword = ref({
    current_password: null,
    password: null,
    password_confirmation: null,
});

const cancelOrderModal = ref(false);
const cancelOrderError = ref('');

const personalInfo = ref({
    first_name: contactData.value.first_name || '',
    last_name: contactData.value.last_name || '',
    email: !contactData.value.banker_email ? '' : (contactData.value.email || ''),
    username: contactData.value.username || '',
});

const mailingAddress = ref({
    address: mailingAddressData.value.address || '',
    address2: mailingAddressData.value.address2 || '',
    bad_address: mailingAddressData.value.bad_address || '',
    city: mailingAddressData.value.city || '',
    id: mailingAddressData.value.id || '',
    phone: mailingAddressData.value.phone || '',
    state: mailingAddressData.value.state || '',
    zip: mailingAddressData.value.zip || '',
    country: mailingAddressData.value.country || 'USA',
});

const ordersInfo = ref({
    orders: ordersData.value || null,
    creditcards: creditcardsData.value || null,
    invoices: invoicesData.value || null,
});

const orderedOrders = computed(() => {
    return ordersInfo.value?.orders.sort((a, b) => {
        return a.order_date < b.order_date ? 1 : -1;
    });
});

const validateCIDCode = (event) => {
    // Update the model only with numeric input, preserving leading zeros
    formOrder.value.ccvercode = event.target.value.replace(/[^0-9]/g, '');
};

function enforceNumeric(event) {
    const value = event.target.value;
    const numericValue = value.replace(/[^0-9]/g, '');

    if (value !== numericValue) {
        event.target.value = numericValue;
        updatePayment.value.ccnum = numericValue;
    }
}

// Filter to format expiration date
const expDate = (value) => {
    if (!value) return '-';
    value = value.toString();
    return value.replace(/(\d\d)(\d\d)/, '$1/$2');
};

// Filter to get the full month name
const getFullMonth = (value) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return value + ' - ' + months[(parseInt(value) - 1)];
};

const ccexpmonths = [
    '01', '02', '03', '04',
    '05', '06', '07', '08',
    '09', '10', '11', '12'
];

const ccexpyears = computed(() => {
    const years = [];
    const currentYear = (new Date()).getFullYear();

    for (let i = 0; i < 18; i++) {
        years.push((currentYear + i).toString());
    }

    return years;
});

const isCreditCardExpired = (order) => {
    const expiration = order.creditcard.expiration.split(' / ');
    const ccexpmonth = Number(expiration[0]);
    const ccexpyear = Number(expiration[1]);
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 2;

    return ccexpyear < currentYear || (ccexpyear === currentYear && ccexpmonth <= currentMonth);
};

const getCreditCardInfo = (order) => {
    return cctypes[order.creditcard.card_type] + ' ' + order.creditcard.last_four + ', Expires: ' + order.creditcard.expiration.replace(' / ', '/');
};

const getOrderStatusTooltip = (order) => {
    return `
        <div class="font-bold mb-3">${order.product_description}</div>
        <div class="mb-1">Status:&nbsp;&nbsp;<span class="float-right italic">${order.order_status}</span></div>
        <div class="mb-1">Created:&nbsp;&nbsp;<span class="float-right italic">${order.order_date}</span></div>
        <div v-show="order.order_id" class="mb-1">Order ID:&nbsp;&nbsp;<span class="float-right italic">${order.order_id}</span></div>
    `;
};

const hasErrors = () => {
    return !(JSON.stringify(errors.value) === "{}" || JSON.stringify(errors.value) === "[]");
};

const updateOrderInformation = (order) => {
    const item = ordersInfo.value.orders.find((mOrder) => mOrder.order_id === order.orderid);
    if (item.credit_card_id == order.cardid) {
        item.creditcard.card_type = Object.keys(cctypes).find(key => key === order.cctype);
        item.creditcard.expiration = order.ccexpmonth + ' / ' + order.ccexpyear;
        item.creditcard.last_four = order.ccnum.slice(-4);
        item.creditcard.name = order.ccname;
    }
};

const openPaymentModal = (order) => {
    updatePaymentModal.value = order.product_description || true;
    updatePaymentError.value = '';
    updatePayment.value = {
        ccname: null,
        cctype: '',
        ccvercode: null,
        ccnum: null,
        ccexpmonth: '',
        ccexpyear: '',
        ccexp: '',
        baaddress: '',
        baaddress2: '',
        bacity: '',
        bastate: '',
        bacountry: '',
        bazip: ''
    };

    if (order.credit_card_id) {
        updatePayment.value.orderid = order.order_id;
        updatePayment.value.cardid = order.creditcard.id;
        updatePayment.value.ccname = order.creditcard.name;
        updatePayment.value.cctype = order.creditcard.card_type;
        updatePayment.value.ccnum = '';//'●●●● ●●●● ●●●● ' + order.creditcard.last_four;

        updatePayment.value.baaddress = order.address.address;
        updatePayment.value.baaddress2 = order.address.address2;
        updatePayment.value.bacity = order.address.city;
        updatePayment.value.bastate = order.address.state;
        updatePayment.value.bazip = order.address.zip;
        updatePayment.value.baphone = order.address.phone;

        updatePayment.value.bacountry = Object.keys(props.states).find(mCountry => props.states[mCountry][order.address.state]);
        if (!updatePayment.value.bacountry) {
            updatePayment.value.bacountry = props.countries.find(mCountry => mCountry['code'] === order.address.state).code;
        }

        let expiration = order.creditcard.expiration.split(' / ');
        let ccexpmonth = expiration[0];
        let ccexpyear = expiration[1];
        updatePayment.value.ccexpmonth = ccexpmonth;
        updatePayment.value.ccexpyear = ccexpyear;
    }
};

const closePaymentModal = () => {
    updatePaymentModal.value = false;
    updatePaymentError.value = '';
    updatePayment.value = {
        ccname: null,
        cctype: '',
        ccvercode: null,
        ccnum: null,
        ccexpmonth: '',
        ccexpyear: '',
        ccexp: '',
        baaddress: '',
        baaddress2: '',
        bacity: '',
        bastate: '',
        bacountry: '',
        bazip: ''
    };
};

const openCancelOrderModal = (order) => {
    if (!order || !order.order_id) {
        flash.value.error('Error getting an order details. Please try again later or reach out to us at support@commoditytrends.com for assistance.');
    } else {
        console.log(order);
        cancelOrderModal.value = order;
        cancelOrderError.value = '';
        cancelOrderInProgress.value = false;
        cancelOrderConfirmed.value = false;
    }
};

const updateCancelOrder = () => {
    if (!cancelOrderModal.value || !cancelOrderModal.value.order_id || !contactData.value.first_name || !contactData.value.last_name || !contactData.value.email) {
        cancelOrderInProgress.value = false;
        cancelOrderError.value = 'Error getting an order details. Please try again later or reach out to us at support@commoditytrends.com for assistance.';
    }

    cancelOrderInProgress.value = true;
    cancelOrderConfirmed.value = false;
    cancelOrderError.value = '';

    const orderPostData = {
        productid: cancelOrderModal.value.product_id,
        orderid: cancelOrderModal.value.order_id,
        reason: 'A cancellation request has been made from commoditytrends.com.',
        fname: contactData.value.first_name,
        lname: contactData.value.last_name,
        email: contactData.value.email,
        phone: mailingAddress.value.phone || 'N/A',
    };

    const route = window.route('main.profile.cancel-order');
    axios.post(route, orderPostData)
        .then((response) => {
            let confirmedMessage = 'We\'re sorry, we were unable to cancel your order at this time. Please try again later or contact support for assistance.';
            if (!response.data.message || response.data.message == '') {
                confirmedMessage = 'We\'re sorry, we were unable to cancel your order at this time. Please try again later or contact support for assistance.';
            } else {
                if (response.data.message) {
                    confirmedMessage = response.data.message.user_message || 'We\'re sorry, we were unable to cancel your order at this time. Please try again later or contact support for assistance.';
                }
            }

            cancelOrderInProgress.value = false;
            cancelOrderConfirmed.value = confirmedMessage;
            cancelOrderError.value = '';
        }).catch(error => {
            let errorMessage = 'We\'re sorry, we were unable to cancel your order at this time. Please try again later or contact support for assistance.';
            if (error.response.data.message && isObjectJSON(error.response.data.message)) {
                let parsedJson = JSON.parse(error.response.data.message);
                errorMessage = parsedJson.user_message || 'We\'re sorry, we were unable to cancel your order at this time. Please try again later or contact support for assistance.';
            } else {
                errorMessage = error.response.data.message || 'We\'re sorry, we were unable to cancel your order at this time. Please try again later or contact support for assistance.';
            }

            cancelOrderError.value = errorMessage;
            cancelOrderInProgress.value = false;
            cancelOrderConfirmed.value = true;
        });
};

const closeCancelOrderModal = () => {
    if (cancelOrderConfirmed.value) {
        setTimeout(() => {
            location.reload();
        }, 150);
    }

    cancelOrderModal.value = false;
    cancelOrderError.value = '';
    cancelOrderInProgress.value = false;
    cancelOrderConfirmed.value = false;
};

const isObjectJSON = (obj) => {
    if (typeof obj !== 'string') {
        obj = JSON.stringify(obj);
    }
    try {
        JSON.parse(obj);
        return true;
    } catch (e) {
        return false;
    }
};

const savePayment = () => {
    isUpdating.value = true;
    updatePaymentError.value = '';
    updatePayment.value.ccexp = `${updatePayment.value.ccexpmonth}${updatePayment.value.ccexpyear.slice(-2)}`;

    const route = window.route('main.profile.update-payment');
    axios.post(route, updatePayment.value)
        .then((response) => {
            updateOrderInformation(updatePayment.value);
            flash.value.display('Successfully updated the Payment information!');
            updatePayment.value = { ccname: null, cctype: '', ccvercode: null, ccnum: null, ccexpmonth: '', ccexpyear: '', ccexp: '', baaddress: '', baaddress2: '', bacity: '', bastate: '', bacountry: '', bazip: '' };
            updatePaymentModal.value = false;
            setTimeout(() => {
                location.reload();
                isUpdating.value = false;
            }, 1500);
        }).catch(error => {
            isUpdating.value = false;
            updatePaymentError.value = (error.response.data.errors) ? error.response.data.errors : [];
            if (!error.response.data.message || error.response.data.message == '') {
                flash.value.error('Error updating Payment information, please try again later.');
            } else {
                if (error.response.data.message && isObjectJSON(error.response.data.message)) {
                    let creditError = JSON.parse(error.response.data.message);
                    flash.value.error(creditError.error_message || 'Error updating Payment information, please try again later.');
                } else {
                    flash.value.error(error.response.data.message);
                }
            }
        }).then(() => {
        });
};

const closePasswordModal = () => {
    changePasswordModal.value = false;
    changePasswordError.value = '';
    changePassword.value = { current_password: null, password: null, password_confirmation: null };
};

const updateEmailBilling = (order) => {
    const route = window.route('main.profile.update-emailbilling');
    const params = {
        'orderid': order.order_id,
        'noticesrequestedpayments': (order.notices_requested_payments == 'False' ? 'true' : 'false'),
    };
    axios.post(route, params)
        .then((response) => {
            order.notices_requested_payments = (order.notices_requested_payments == 'False') ? 'True' : 'False';
            flash.value.display('Successfully updated the Email Billing Notifications!');
        }).catch(error => {
            changePasswordError.value = (error.response.data.errors) ? error.response.data.errors : [];
            if (!error.response.data.message || error.response.data.message == '') {
                flash.value.error('Error updating Email Billing Notifications, please try again later.');
            } else {
                flash.value.error(error.response.data.message);
            }
        }).then(() => {
        });
};

const updatePassword = () => {
    isUpdating.value = true;
    changePasswordError.value = '';

    const route = window.route('main.profile.update-password');
    axios.post(route, changePassword.value)
        .then((response) => {
            isUpdating.value = false;
            changePassword.value = { current_password: null, password: null, password_confirmation: null };
            changePasswordModal.value = false;
            flash.value.display('Successfully updated the Password!');
        }).catch(error => {
            isUpdating.value = false;
            changePasswordError.value = (error.response.data.errors) ? error.response.data.errors : [];
            if (!error.response.data.message || error.response.data.message == '') {
                flash.value.error('Error updating Password, please try again later.');
            } else {
                flash.value.error(error.response.data.message);
            }
        }).then(() => {
        });
};

const update = (type) => {
    isUpdating.value = true;
    errors.value = [];
    const route = window.route('main.profile.update');

    let formData = {};
    if (type == 'address') {
        mailingAddress.value.type = type;
        formData = mailingAddress.value;
    } else {
        personalInfo.value.type = type;
        formData = personalInfo.value;
    }

    formData.contactData = contactData.value;

    axios.post(route, formData)
        .then((response) => {
            isUpdating.value = false;
            flash.value.display('Successfully updated «Profile Settings»!');

            // update contactData
            contactData.value['first_name'] = formData['first_name'];
            contactData.value['last_name'] = formData['last_name'];
            contactData.value['email'] = formData['email'];
            contactData.value['username'] = formData['username'];

            close();
        }).catch(error => {
            isUpdating.value = false;
            errors.value = (error.response.data.errors) ? error.response.data.errors : [];
            if (!error.response.data.message || error.response.data.message == '') {
                flash.value.error('Error updating «Profile Settings», please try again later.');
            } else {
                flash.value.error(error.response.data.message);
            }
        }).then(() => {
        });
};

const getInvoice = (invoice_date) => {
    axios.get(window.route('main.profile.get-invoice', { 'invoice_date': invoice_date }), { responseType: 'blob' }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'CommodityTrends-invoice-' + invoice_date + '.pdf');
        document.body.appendChild(link);
        link.click();
    })
        .catch(error => {
            console.error(error);
        });
};

const editProfile = (type) => {
    editProfileType.value = type;
    isEditMode.value = true;
};

const close = () => {
    editProfileType.value = false;
    isEditMode.value = false;
};
</script>

<style lang="scss">
#profileForm {
    .profile-btn-edit {
        &:hover {
            @apply border-yellow-500 text-yellow-500;
        }

        &>b {
            @apply hidden;
        }

        &:hover {
            &>b {
                @apply inline-block;
            }
        }
    }

    fieldset:disabled {
        .profile-actions {
            @apply hidden;
        }
    }

    button[disabled],
    div[disabled] {
        opacity: 0.7 !important;
        pointer-events: none !important;
        cursor: not-allowed !important;
    }

    table {

        thead,
        tfoot {

            td,
            th {
                @apply px-3 py-2;
            }
        }

        thead {
            tr {

                td,
                th {
                    @apply text-center;
                }
            }
        }

        tbody {
            tr {
                @apply border-b border-gray-100;

                td,
                th {
                    @apply border-0 align-middle;
                    @apply px-3 py-2;
                }

                td:not(.text-left),
                th:not(.text-left) {
                    @apply text-center;
                }
            }
        }

        .sortable {
            @apply cursor-pointer;
        }
    }
}
</style>
